import { useState, useEffect, useCallback } from 'react';
import { X, Sparkles, Info, AlertTriangle, CheckCircle, ExternalLink, ChevronLeft, ChevronRight } from 'lucide-react';
import { widgetApi } from '../lib/api';

const TYPE_STYLES = {
  info: {
    bg: 'bg-blue-600',
    text: 'text-white',
    icon: Info,
  },
  success: {
    bg: 'bg-green-600',
    text: 'text-white',
    icon: CheckCircle,
  },
  warning: {
    bg: 'bg-amber-500',
    text: 'text-white',
    icon: AlertTriangle,
  },
  new_feature: {
    bg: 'bg-gradient-to-r from-purple-600 to-indigo-600',
    text: 'text-white',
    icon: Sparkles,
  },
};

export default function AnnouncementBanner() {
  const [announcements, setAnnouncements] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isVisible, setIsVisible] = useState(true);
  const [dismissedIds, setDismissedIds] = useState(() => {
    const stored = localStorage.getItem('dismissedAnnouncements');
    return stored ? JSON.parse(stored) : [];
  });

  // Fetch announcements (keep only the 3 most recent, newest first)
  useEffect(() => {
    const fetchAnnouncements = async () => {
      try {
        const res = await widgetApi.getActiveAnnouncements();
        // Sort by created_at desc, take only the 3 most recent
        const sorted = [...(res.data || [])].sort((a, b) => {
          const da = new Date(a.created_at || 0).getTime();
          const db = new Date(b.created_at || 0).getTime();
          return db - da;
        });
        const top3 = sorted.slice(0, 3);
        // Filter out dismissed announcements
        const activeAnnouncements = top3.filter(
          (ann) => !dismissedIds.includes(ann.id)
        );
        setAnnouncements(activeAnnouncements);
      } catch (error) {
        console.error('Error fetching announcements:', error);
      }
    };
    fetchAnnouncements();
  }, [dismissedIds]);

  // Auto-rotate announcements
  useEffect(() => {
    if (announcements.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % announcements.length);
    }, 5000); // Change every 5 seconds

    return () => clearInterval(interval);
  }, [announcements.length]);

  // Navigate to previous/next
  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => 
      prev === 0 ? announcements.length - 1 : prev - 1
    );
  }, [announcements.length]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % announcements.length);
  }, [announcements.length]);

  // Dismiss current announcement
  const dismissCurrent = useCallback(() => {
    if (announcements.length === 0) return;
    
    const currentAnn = announcements[currentIndex];
    const newDismissedIds = [...dismissedIds, currentAnn.id];
    setDismissedIds(newDismissedIds);
    localStorage.setItem('dismissedAnnouncements', JSON.stringify(newDismissedIds));
    
    // Move to next announcement or hide if none left
    if (announcements.length === 1) {
      setIsVisible(false);
    } else {
      setCurrentIndex((prev) => prev >= announcements.length - 1 ? 0 : prev);
    }
  }, [announcements, currentIndex, dismissedIds]);

  // Don't render if no announcements or not visible
  if (!isVisible || announcements.length === 0) {
    return null;
  }

  const currentAnnouncement = announcements[currentIndex];
  const style = TYPE_STYLES[currentAnnouncement.type] || TYPE_STYLES.info;
  const IconComponent = style.icon;

  return (
    <div 
      className={`${style.bg} ${style.text} relative overflow-hidden z-[60]`}
      data-testid="announcement-banner"
    >
      <div className="max-w-7xl mx-auto px-4 py-2">
        <div className="flex items-center justify-center gap-3">
          {/* Navigation left */}
          {announcements.length > 1 && (
            <button
              onClick={goToPrevious}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Annonce précédente"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
          )}

          {/* Icon */}
          <IconComponent className="w-4 h-4 shrink-0" />

          {/* Message with animation */}
          <div className="flex-1 text-center overflow-hidden">
            <p className="text-sm font-medium truncate animate-fade-in">
              {currentAnnouncement.message}
              {currentAnnouncement.link && (
                <a
                  href={currentAnnouncement.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 ml-2 underline hover:no-underline font-semibold"
                >
                  {currentAnnouncement.link_text || 'En savoir plus'}
                  <ExternalLink className="w-3 h-3" />
                </a>
              )}
            </p>
          </div>

          {/* Navigation right */}
          {announcements.length > 1 && (
            <button
              onClick={goToNext}
              className="p-1 hover:bg-white/20 rounded-full transition-colors"
              aria-label="Annonce suivante"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          )}

          {/* Pagination dots */}
          {announcements.length > 1 && (
            <div className="flex items-center gap-1 mx-2">
              {announcements.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setCurrentIndex(idx)}
                  className={`w-1.5 h-1.5 rounded-full transition-all ${
                    idx === currentIndex 
                      ? 'bg-white w-3' 
                      : 'bg-white/50 hover:bg-white/70'
                  }`}
                  aria-label={`Aller à l'annonce ${idx + 1}`}
                />
              ))}
            </div>
          )}

          {/* Close button */}
          <button
            onClick={dismissCurrent}
            className="p-1 hover:bg-white/20 rounded-full transition-colors ml-2"
            aria-label="Fermer l'annonce"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Animated gradient overlay for new_feature type */}
      {currentAnnouncement.type === 'new_feature' && (
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent animate-shimmer pointer-events-none" />
      )}

      <style jsx>{`
        @keyframes fade-in {
          from { opacity: 0; transform: translateY(-5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(100%); }
        }
        .animate-fade-in {
          animation: fade-in 0.3s ease-out;
        }
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
      `}</style>
    </div>
  );
}
